import React, { useEffect, useRef, useState } from 'react';
import CustomTable from '../../share-components/CustomTable';
import { useNavigate } from 'react-router-dom';
import { PAGE_PURCHASE_LIST, ROUTE_PURCHASE_DETAILS, ROUTE_PURCHASE_EDIT, ROUTE_PURCHASE_LIST } from '../../constants/routeConstants';
import { Accordion, Button, Card } from 'react-bootstrap';
import { useAppContext } from '../../share-components/AppContext';
import {
    currentMonth, currentYear, customStorage, formatDateTime, getAvailableMonths, getChangingSelectColor, getPayloadData,
    handleFromMonthChange, handleFromYearChange, handleToMonthChange, handleToYearChange, isEqualNumber, isNullOrEmptyString, years
} from '../../services/UtilityService';
import { getActiveCategories, getPurchaseProducts, receivedPurchasedItem } from '../../services/ManagementService';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import AlertDialog from '../../share-components/AlertDialog';
import { SELECT_ALL_DEFAULT, SERVER_ERROR_MESSAGE, TBL_DATA_SEPARATOR, TOKEN_SESSION_KEY } from '../../constants/commonConstants';
import useScrollRestoration from '../../share-components/UseScrollRestoration';
import SearchColumnSelect from '../../share-components/SearchColumnSelect';
import NoDataFound from '../../share-components/NoDataFound';
import FormTitle from '../../share-components/FormTitle';
import noImage from '../../assets/No_Image_Available.jpg';
import { getAllActiveUsers } from '../../services/CommonService';

const PurchaseList = () => {

    useScrollRestoration(PAGE_PURCHASE_LIST);

    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const initialSearchDataObj = sessionStorage.getItem(ROUTE_PURCHASE_LIST);
    const initialSearchData = initialSearchDataObj ? JSON.parse(initialSearchDataObj) : null;

    const { loading, setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [originalPurchaseList, setOriginalPurchaseList] = useState([]);
    const [purchasedTblList, setPurchasedTblList] = useState([]);
    const [productPictureList, setProductPictureList] = useState([]);

    const [activeUsers, setActiveUsers] = useState([]);
    const [purchasedById, setPurchasedById] = useState(!initialSearchData ? (currentUser?.userId || "") : initialSearchData?.purchasedById);
    const [categories, setCategories] = useState([]);
    const [categoryId, setCategoryId] = useState(initialSearchData?.categoryId || "");
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);

    const [fromYear, setFromYear] = useState(initialSearchData?.fromYear || currentYear);
    const [fromMonth, setFromMonth] = useState(initialSearchData?.fromMonth || currentMonth);
    const [toYear, setToYear] = useState(initialSearchData?.toYear || currentYear);
    const [toMonth, setToMonth] = useState(initialSearchData?.toMonth || currentMonth);

    const [receivedPurchaseId, setReceivedPurchaseId] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [searchData, setSearchData] = useState(null);

    const [summaryInfo, setSummaryInfo] = useState({});

    const [afterGetDataStore, setAfterGetDataStore] = useState({
        fromYear: initialSearchData?.fromYear || currentYear,
        fromMonth: initialSearchData?.fromMonth || currentMonth,
        toYear: initialSearchData?.toYear || currentYear,
        toMonth: initialSearchData?.toMonth || currentMonth,
        purchasedById: !initialSearchData ? (currentUser?.userId || "") : initialSearchData?.purchasedById,
        categoryId: initialSearchData?.categoryId || ""
    });

    const navigate = useNavigate();

    useEffect(() => {
        const searchData = {
            fromYear,
            fromMonth,
            toYear,
            toMonth,
            purchasedById,
            categoryId,
        };
        setSearchData(searchData);
    }, [loading]);

    useEffect(() => {
        setRouteNaviData("PURCHASE >> PURCHASE LIST");
        fetchInitialLoad();
    }, []);

    const fetchInitialLoad = async () => {
        setLoading(true);
        try {
            await fetchActiveUsers();
            const categoryList = await getActiveCategories();
            let sortedCategories = (categoryList || [])?.sort((a, b) => (a.categoryName || "")?.localeCompare((b?.categoryName || "")));
            setCategories(sortedCategories);
            await fetchPurchasedProducts();
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const fetchActiveUsers = async () => {
        const response = await getAllActiveUsers();
        const sortedUsers = (response || [])?.sort((a, b) => (a.username || "")?.localeCompare((b?.username || "")));
        setActiveUsers(sortedUsers);
    }

    const fetchPurchasedProducts = async () => {
        setLoading(true);
        try {
            const searchDates = {
                fromYear: parseInt(fromYear),
                fromMonth: parseInt(fromMonth),
                toYear: parseInt(toYear),
                toMonth: parseInt(toMonth),
                purchasedById: purchasedById || null,
                categoryId: parseInt(categoryId) || null
            }
            const response = await getPurchaseProducts(searchDates);

            if (response) {
                const pcInfoList = response?.purchasedDataInfoList || [];
                const pictureList = response?.productPictureInfoList || [];
                setProductPictureList(pictureList);
                setOriginalPurchaseList(pcInfoList);
                const afterGetData = {
                    fromYear,
                    fromMonth,
                    toYear,
                    toMonth,
                    purchasedById,
                    categoryId
                }
                setAfterGetDataStore(afterGetData);
            }
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const sortedPurchasedList = (originalPurchaseList || []).sort((a, b) => {
            const dateA = a?.purchasedInfo?.lastUpdatedDateTime || '';
            const dateB = b?.purchasedInfo?.lastUpdatedDateTime || '';
            return dateB.localeCompare(dateA);
        });
        const convertedResponse = (sortedPurchasedList || []).map((pcData) => ({
            ...pcData?.purchasedInfo,
            showData: getShowData(pcData)
        }));
        setPurchasedTblList(convertedResponse);
    }, [originalPurchaseList]);

    const getShowData = (purchasedData) => {
        const pcInfo = purchasedData?.purchasedInfo;
        const itemSearchInfo = (purchasedData?.purchasedItems || []).map(item =>
            [item.brandName || "", item.categoryName || "", item.productName || "", item.color ? `[${item.color}]` : "", item.size ? `[${item.size}]` : ""] || []
        );
        // Generate plain text for searchability
        const plainText = [
            `${pcInfo?.purchasedBy || ""}`,
            // `Count: (${purchasedData?.totalQuantity})`,
            `Actual Paid: ${`${pcInfo.totalPurchasedAmount - pcInfo.discountAmount}￥${pcInfo.totalPurchasedAmount - pcInfo?.discountAmount}¥`}`,
            parseInt(pcInfo?.discountAmount) > 0 ? `Discount: ${`${pcInfo.discountAmount}￥${pcInfo.discountAmount}¥`}` : "",
            formatDateTime(pcInfo?.createdDateTime),
            pcInfo?.createdDateTime !== pcInfo?.lastUpdatedDateTime ? formatDateTime(pcInfo?.lastUpdatedDateTime) : "",
            pcInfo?.receivedFlag === true ? "Arrived" : "Not Received Yet",
            itemSearchInfo.join(TBL_DATA_SEPARATOR),
        ].filter(Boolean).join(TBL_DATA_SEPARATOR); // Join all values into a single string
        return {
            jsx: (
                <div style={{ fontWeight: '600' }}>
                    {/* <div className='main-name-block'><i className='bi bi-person-add'></i> {pcInfo?.purchasedBy} <i className='bi-person-add'></i></div><hr className='m-0' /> */}
                    <div style={{ color: 'blue' }}><i className='bi bi-person-add'></i> {pcInfo?.purchasedBy}</div><hr className='m-0' />
                    <div style={{ color: 'blue' }}><i className='bi bi-cash'></i> Actual Paid: {(pcInfo?.totalPurchasedAmount - pcInfo?.discountAmount) || 0}¥</div><hr className='m-0' />
                    {parseInt(pcInfo?.discountAmount) > 0 && <><div style={{ color: 'blue' }}><i className='bi bi-cash-coin'></i> Discount: {pcInfo?.discountAmount || 0}¥</div><hr className='m-0' /></>}
                    <div><i className="bi bi-shop"></i> {formatDateTime(pcInfo?.createdDateTime)}</div><hr className='m-0' />
                    {pcInfo?.createdDateTime !== pcInfo?.lastUpdatedDateTime &&
                        <>
                            <div><i className="bi bi-alarm"></i> {formatDateTime(pcInfo?.lastUpdatedDateTime)}</div><hr className='m-0' />
                        </>
                    }
                    <div>{pcInfo?.receivedFlag === true ? <i className='bi bi-flag'></i> :
                        <i className='bi bi-truck'></i>} {pcInfo?.receivedFlag === true ? "Arrived" : "Not Received Yet"}
                    </div>

                    {pcInfo?.comment && <div className='stockAccordion no-pad mb-1'>
                        <Accordion >
                            <Accordion.Item eventKey="0" >
                                <Accordion.Header>
                                    <div>
                                        <i className='bi bi-chat-dots'></i> Comment
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className='stockAndInStockDetailsBlock p-2'>
                                        {pcInfo?.comment}
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                        <hr className='m-0' />
                    </div>}

                    <div className='stockAccordion no-pad'>
                        <Accordion >
                            <Accordion.Item eventKey="0" >
                                <Accordion.Header>
                                    <div>
                                        <i className='bi bi-bag-check-fill'></i> Purchased ({pcInfo?.totalQuantity}) Item{pcInfo?.totalQuantity > 1 ? 's' : ''}
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className='stockAndInStockDetailsBlock'>
                                        {(purchasedData?.purchasedItems || []).map((item, index) => {
                                            const productPicture = productPictureList?.find(data => data.productId == item.productId)?.productPicture;
                                            return (
                                                <div key={index} style={{
                                                    border: "1px solid black",
                                                    padding: "5px",
                                                    marginBottom: "1px",
                                                    borderRadius: "5px"
                                                }}>
                                                    <div style={{ display: 'flex' }}>
                                                        <div style={{ paddingRight: '10px', flex: "1" }}>
                                                            <div><i className='bi bi-award'></i> {item?.brandName}</div><hr className='m-0' />
                                                            <div><i className='bi bi-box'></i> {item?.productName}</div><hr className='m-0' />
                                                            <div><i className='bi bi-tags'></i> {item?.categoryName}</div><hr className='m-0' />
                                                            {(!isNullOrEmptyString(item?.size) || !isNullOrEmptyString(item.color)) &&
                                                                <>
                                                                    <div>
                                                                        <i className='bi bi-palette'></i> {!isNullOrEmptyString(item.color) && <span>[{item.color}]</span>}{!isNullOrEmptyString(item?.size) && <span>[{item.size}]</span>}

                                                                    </div><hr className='m-0' />
                                                                </>
                                                            }
                                                            <div><i className='bi bi-bag-plus'></i> x({item?.quantity})</div><hr className='m-0' />
                                                            <div><i className='bi bi-cash'></i><span> {(item.amount || 0)}¥</span></div>
                                                        </div>
                                                        <div style={{ paddingTop: "5px" }}>
                                                            <img
                                                                src={productPicture ? `data:image/jpeg;base64,${productPicture}` : noImage}
                                                                alt="picture"
                                                                className="img-thumbnail"
                                                                style={{ width: "var(--orderProductPictureWidth)", height: "var(--orderProductPictureHeight)", objectFit: "cover" }}
                                                            />
                                                        </div>
                                                    </div>

                                                </div>
                                            )
                                        })}
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                        <hr className='m-0' />
                    </div>
                </div>
            ),
            text: plainText.toLowerCase() // Store searchable text
        };
    };

    const columns = [
        {
            Header: 'Purchased Infos',
            accessor: 'showData',
            disableSortBy: true,
            style: {
                minWidth: "150px",
                maxWidth: "350px"
            },
            Cell: ({ row }) => row.original.showData.jsx,
        },
        {
            Header: 'Status',
            accessor: 'status',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center align-middle',
            style: {
                minWidth: "50px",
                // maxWidth: "100px"
            },
            Cell: ({ row }) => (
                <div className='center-block'>
                    {row.original?.purchasedById !== currentUser?.userId ?
                        <Button variant="danger" disabled
                            className="btn-sm icon-btn-vertical">
                            <i className="bi bi-ban"></i>
                            <span>Disabled</span>
                        </Button> : row.original.receivedFlag !== true ?
                            <Button
                                variant="primary"
                                className="btn-sm icon-btn-vertical"
                                // style={{
                                //     whiteSpace: 'nowrap'
                                // }}
                                onClick={() => onReceivedClick(row.original?.purchaseId)}
                            >
                                <i className="bi bi-flag"></i>
                                <span>Receive</span>
                            </Button> :
                            <span className='icon-btn-vertical'>Arrived</span>}

                </div>
            ),
        },
        {
            Header: 'Edit',
            accessor: 'edit',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center align-middle',
            style: {
                minWidth: "70px",
                // maxWidth: "100px"
            },
            Cell: ({ row }) => (
                <div className='center-block'>
                    {row.original?.purchasedById === currentUser?.userId ? <Button
                        variant="secondary"
                        className="btn-sm icon-btn-vertical"
                        onClick={() => navigate(`${ROUTE_PURCHASE_EDIT}/${row.original?.purchaseId}`)}
                    >
                        <i className="bi bi-pencil-square"></i>
                        <span>Edit</span>
                    </Button> :
                        <Button variant="danger" disabled
                            className="btn-sm icon-btn-vertical">
                            <i className="bi bi-ban"></i>
                            <span>Disabled</span>
                        </Button>
                    }
                </div>
            ),
        },
        {
            Header: 'Details',
            accessor: 'details',
            unSearchable: true,
            disableSortBy: true,
            headerClassName: 'disableSort text-center',
            className: 'text-center align-middle',
            style: {
                minWidth: "50px",
                // maxWidth: "100px"
            },
            Cell: ({ row }) => (
                <div className='center-block'>
                    <Button
                        variant="secondary"
                        className="btn-sm icon-btn-vertical"
                        onClick={() => navigate(`${ROUTE_PURCHASE_DETAILS}/${row.original?.purchaseId}`)}
                    >
                        <i className="bi bi-info-circle"></i>
                        <span>Details</span>
                    </Button>
                </div>
            ),
        },
    ];

    const onReceivedClick = (purchaseId) => {
        setReceivedPurchaseId(purchaseId);
        setShowConfirmDialog(true);
    }

    const handleReceived = async () => {
        const purchaseIdNumber = parseInt(receivedPurchaseId) || 0;
        if (purchaseIdNumber <= 0) {
            return;
        }
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            await receivedPurchasedItem(purchaseIdNumber);
            setShowSuccessDialog(true);

        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setReceivedPurchaseId(null);
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = async () => {
        setShowSuccessDialog(false);
        await fetchInitialLoad();
    }

    useEffect(() => {
        const listData = filteredData || [];
        const totalPurchasedAmount = listData?.reduce((sum, item) => sum + item.totalPurchasedAmount, 0);
        const totalDiscountAmount = listData?.reduce((sum, item) => sum + item.discountAmount, 0);
        const totalPurchasedQuantity = listData?.reduce((sum, item) => sum + item.totalQuantity, 0);
        const summary = {
            totalPurchasedAmount,
            totalDiscountAmount,
            totalPurchasedQuantity
        }
        setSummaryInfo(summary);
    }, [filteredData]);

    return (
        <div id='purchaseListPage'>
            <FormTitle title={"Purchase List"} isCenter={true} />
            {isFirstLoaded && <>
                <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {afterGetDataStore?.fromYear}/{afterGetDataStore?.fromMonth} ～ {afterGetDataStore?.toYear}/{afterGetDataStore?.toMonth}
                </div>
                <div style={{ overflow: "auto", paddingBottom: "50px", }}>
                    <div style={{ minWidth: "max-content", maxWidth: "650px", margin: '0 auto' }}>
                        <Card>
                            <Card.Body style={{ padding: '10px 5px 20px 5px' }}>
                                <div>
                                    <div className='mt-1 mb-1' style={{ display: 'flex', justifyContent: 'start', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                                        {/* Purchased By */}
                                        <div className="d-inline">
                                            <label className="me-1 custom-label" htmlFor="purchasedBy" style={{ minWidth: "50px" }}>
                                                Purchased By
                                            </label>
                                            <div>
                                                <select
                                                    id="purchasedBy"
                                                    className={`customInputControl select-min-max-width ${getChangingSelectColor(purchasedById, afterGetDataStore?.purchasedById)}`}
                                                    value={purchasedById}
                                                    onChange={(e) => setPurchasedById(e.target.value || '')}
                                                >
                                                    <option value="">{SELECT_ALL_DEFAULT}</option>
                                                    {activeUsers.map(activeUser => (
                                                        <option key={activeUser.userId} value={activeUser.userId}>{activeUser.username}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="d-inline">
                                            <label className="me-1 custom-label" htmlFor="category" style={{ minWidth: "50px" }}>
                                                Category
                                            </label>
                                            <div>
                                                <select
                                                    id="category"
                                                    className={`customInputControl select-min-max-width ${getChangingSelectColor(categoryId, afterGetDataStore?.categoryId)}`}
                                                    value={categoryId}
                                                    onChange={(e) => setCategoryId(e.target.value || '')}
                                                >
                                                    <option value="">{SELECT_ALL_DEFAULT}</option>
                                                    {categories.map(category => (
                                                        <option key={category.categoryId} value={category.categoryId}>{category.categoryName}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-1 mb-1" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                        {/* From Year & Month */}
                                        <div className="d-inline">
                                            <label className="me-1 custom-label" htmlFor="fromYear" style={{ minWidth: "50px" }}>
                                                From
                                            </label>
                                            <div>
                                                <select
                                                    id="fromYear"
                                                    className={`customInputControl ${getChangingSelectColor(fromYear, afterGetDataStore?.fromYear)}`}
                                                    value={fromYear}
                                                    onChange={(e) => handleFromYearChange(e, fromMonth, toYear, toMonth, setFromYear, setFromMonth, setToYear, setToMonth)}
                                                >
                                                    {years.map((year) => (
                                                        <option key={year} value={year}>{year}</option>
                                                    ))}
                                                </select>
                                                <span className="separator">/</span>
                                                <select
                                                    id="fromMonth"
                                                    className={`customInputControl ${getChangingSelectColor(fromMonth, afterGetDataStore?.fromMonth)}`}
                                                    value={fromMonth}
                                                    onChange={(e) => handleFromMonthChange(e, fromYear, toYear, toMonth, setFromMonth, setToMonth)}
                                                >
                                                    {getAvailableMonths(fromYear).map((month) => (
                                                        <option key={month} value={month}>
                                                            {month.toString().padStart(2, "0")}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* To Year & Month */}
                                        <div className="d-inline">
                                            <label className="me-1 custom-label" htmlFor="toYear" style={{ minWidth: "50px" }}>
                                                To
                                            </label>
                                            <div>
                                                <select
                                                    id="toYear"
                                                    className={`customInputControl ${getChangingSelectColor(toYear, afterGetDataStore?.toYear)}`}
                                                    value={toYear}
                                                    onChange={(e) => handleToYearChange(e, fromYear, fromMonth, toMonth, setFromYear, setToYear, setFromMonth)}
                                                >
                                                    {years.map((year) => (
                                                        <option key={year} value={year}>{year}</option>
                                                    ))}
                                                </select>
                                                <span className="separator">/</span>
                                                <select
                                                    id="toMonth"
                                                    className={`customInputControl ${getChangingSelectColor(toMonth, afterGetDataStore?.toMonth)}`}
                                                    value={toMonth}
                                                    onChange={(e) => handleToMonthChange(e, fromYear, fromMonth, toYear, setFromMonth, setToMonth)}
                                                >
                                                    {getAvailableMonths(toYear).map((month) => (
                                                        <option key={month} value={month}>
                                                            {month.toString().padStart(2, "0")}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-inline">
                                        <button type='button' className="btn btn-success btn-sm appBtn"
                                            onClick={fetchPurchasedProducts}
                                        >Get Data</button>
                                    </div>

                                    <div className='summaryAccordion mt-1'>
                                        <Accordion  >
                                            <Accordion.Item eventKey="0">
                                                <Accordion.Header>
                                                    <div style={{ minWidth: '258px' }}>
                                                        Summary Info
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <div>
                                                        <table className='table table-bordered auto-width-table'>
                                                            <tbody>
                                                                <tr><td>Actual Paid Amount:</td><td>{summaryInfo?.totalPurchasedAmount}¥</td></tr>
                                                                <tr><td>Discount Amount:</td><td>{summaryInfo?.totalDiscountAmount}¥</td></tr>
                                                                <tr><td>Purchased Quantity:</td><td>{summaryInfo?.totalPurchasedQuantity}</td></tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        </Accordion>
                                    </div>

                                    <div style={{ display: 'block', marginBottom: '10px', marginTop: '10px' }}>
                                        <SearchColumnSelect data={purchasedTblList} setData={setFilteredData} columns={columns}
                                            isNoColumn={true} storeSessionKey={ROUTE_PURCHASE_LIST} sessionData={searchData}
                                        />
                                    </div>
                                    {filteredData?.length > 0 ?
                                        <CustomTable
                                            tableId='purchaseListTbl'
                                            columns={columns}
                                            tblData={filteredData || []}
                                            showPagination={true}
                                            itemsPerPage={10}
                                            initialSortBy={[{ id: 'showData', desc: false }]}
                                            noHeader={true}
                                            isStorePageData={true}
                                        /> : <NoDataFound />
                                    }
                                </div>
                            </Card.Body>
                        </Card>


                    </div>
                </div>
                <ConfirmDialog
                    dialogId='confirmDialog'
                    showDialog={(parseInt(receivedPurchaseId) || 0 > 0) && showConfirmDialog}
                    // title='Logout Dialog'
                    body={'Are you sure you received this purchased item(s)?'}
                    onConfirm={handleReceived}
                    onCancel={() => { setReceivedPurchaseId(null); setShowConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={'Your purchase received status is successfully updated.'}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
};

export default PurchaseList;